import React from 'react';
import LegalPageLayout, { H2, H3, P, UL } from '../../components/LegalPageLayout';

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="May 31, 2026" testId="privacy-policy-page">
      <P>
        SecondDoctorOpinion.com ("we", "us", "our") is committed to protecting the privacy and security of
        your personal and health information. This policy explains how we collect, use, store, and share your
        information in compliance with the Digital Personal Data Protection Act, 2023 (DPDP Act, India) and
        applicable medical confidentiality standards.
      </P>

      <H2>1. Information We Collect</H2>
      <H3>Account Information</H3>
      <UL>
        <li>Name, email, mobile number, password (hashed using bcrypt)</li>
        <li>For doctors: medical qualifications, Medical Council registration number, years of experience, hospital affiliation</li>
      </UL>
      <H3>Medical Information</H3>
      <UL>
        <li>Symptoms, current diagnosis, medical history, medications, allergies</li>
        <li>Medical records you upload: PDF reports, scans (JPG, PNG, DICOM), lab reports</li>
        <li>Specialty preference and tier selected</li>
      </UL>
      <H3>Payment Information</H3>
      <UL>
        <li>We do <strong>not</strong> store your card or UPI credentials.</li>
        <li>All card payments are processed by Stripe (PCI-DSS compliant). We only retain transaction reference IDs and amount paid.</li>
      </UL>

      <H2>2. How We Use Your Information</H2>
      <UL>
        <li>To match your case with an appropriate specialist by specialty</li>
        <li>To enable the assigned specialist to review your records and provide an opinion</li>
        <li>To send case status updates and the final opinion report</li>
        <li>To process payments and issue receipts</li>
        <li>To generate AI-assisted preliminary analyses (using Anthropic Claude Sonnet 4.5) which are then verified by a human specialist</li>
        <li>To improve platform safety, prevent fraud, and comply with legal obligations</li>
      </UL>

      <H2>3. Who Has Access to Your Information</H2>
      <UL>
        <li>The specialist assigned to your case</li>
        <li>Authorized administrative staff for case routing and customer support</li>
        <li>Our infrastructure providers (Stripe for payments, Anthropic for AI inference, object storage for medical files) under appropriate data processing agreements</li>
        <li>We will <strong>never</strong> share your records with your primary doctor unless you explicitly request it.</li>
        <li>We do <strong>not</strong> sell your data to third parties for advertising or any other purpose.</li>
      </UL>

      <H2>4. Data Security</H2>
      <UL>
        <li>All data transmitted to and from our servers is encrypted using HTTPS/TLS.</li>
        <li>Passwords are stored only as bcrypt hashes — never in plain text.</li>
        <li>Medical files are stored in encrypted object storage with role-based access controls.</li>
        <li>Authentication uses short-lived JWT access tokens and httpOnly cookies to prevent XSS-based theft.</li>
        <li>Access to your records is logged for audit purposes.</li>
      </UL>

      <H2>5. Your Rights (DPDP Act)</H2>
      <UL>
        <li><strong>Right to access:</strong> Request a copy of the personal data we hold about you</li>
        <li><strong>Right to correction:</strong> Update inaccurate information</li>
        <li><strong>Right to erasure:</strong> Request deletion of your account and associated data (subject to legal retention requirements)</li>
        <li><strong>Right to grievance redressal:</strong> Submit a complaint via our Grievance Redressal page</li>
        <li><strong>Right to nominate:</strong> Nominate another individual to exercise your rights in case of incapacity</li>
      </UL>
      <P>
        Email <strong>privacy@seconddoctoropinion.com</strong> to exercise any of these rights. We will respond within 7 business days.
      </P>

      <H2>6. Data Retention</H2>
      <P>
        We retain your medical records for at least 7 years from the date of consultation to comply with
        Indian medical record retention standards. After this period, you may request deletion. Payment
        records are retained as required by applicable financial regulations.
      </P>

      <H2>7. Cookies</H2>
      <P>
        We use only essential cookies required for authentication (httpOnly access and refresh tokens). We do
        not use third-party advertising cookies or analytics that track you across other websites.
      </P>

      <H2>8. Children</H2>
      <P>
        Our service is intended for users 18 years and older. For consultations involving minors, the case
        must be submitted by a parent or legal guardian on the minor's behalf, with full legal responsibility
        assumed by the guardian.
      </P>

      <H2>9. Changes to this Policy</H2>
      <P>
        We may update this Privacy Policy from time to time. Material changes will be communicated via email
        and a notice on this page at least 7 days before they take effect.
      </P>
    </LegalPageLayout>
  );
}
